<template>
    <div ref="rootElement">
        <div class="opPaginationContainer top" v-if="showUpperPagination">
            <div class="opSvuotaFiltri"></div>
            <div class="opPaginationNumberContainer">
                <div class="opPaginationNumber" @click="subtractPage">&lt;</div>
                <div class="opPaginationNumber" v-for="i in pages" :key="i" :class="{selected: selectedPage == i}" @click="setSelectedPage(i)">{{i}}</div>
                <div class="opPaginationNumber" @click="addPage">&gt;</div>
                <select class="opItemsPerPage" v-model="xItemsPerPage">
                    <option :value="10">10</option><option :value="25">25</option><option :value="50">50</option><option :value="100">100</option>
                </select>
            </div>
            <div class="opItemsTotal">Totale: <span class="opItemsTotalText">{{totalItemsCount}}</span></div>
        </div>

        <div class="mb-4" style="width:100%; overflow-x:auto">
            <table class="opTable" ref="tableRoot">
                <slot :items="items"></slot>
            </table>
        </div>

        <div class="opPaginationContainer bottom" v-if="showLowerPagination">
            <div class="opSvuotaFiltri"></div>
            <div class="opPaginationNumberContainer">
                <div class="opPaginationNumber" @click="subtractPage">&lt;</div>
                <div class="opPaginationNumber" v-for="i in pages" :key="i" :class="{selected: selectedPage == i}" @click="setSelectedPage(i)">{{i}}</div>
                <div class="opPaginationNumber" @click="addPage">&gt;</div>
                <select class="opItemsPerPage" v-model="xItemsPerPage">
                    <option :value="10">10</option><option :value="25">25</option><option :value="50">50</option><option :value="100">100</option>
                </select>
            </div>
            <div class="opItemsTotal">Totale: <span class="opItemsTotalText">{{totalItemsCount}}</span></div>
        </div>
    </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { OP_TABLE_CTX, type OpTableContext, type SortDir, type ColumnMeta } from './opTable.context';

@Options({
  provide() {
    const self = this as any;
    const ctx: OpTableContext = {
      getFilterValue: (key) => self.uiFilterShadow[key], // shadow per ripopolare gli input
      setFilterValue: (key, value, meta?: ColumnMeta) => {
        self.uiFilterShadow[key] = value;

        if (!meta) meta = {};
        // mappatura su this.filter come facevi prima
        if (meta.filterType === 'dateRange') {
          self.filter[meta.fromKey!] = value?.from ? new Date(value.from).toISOString() : null;
          self.filter[meta.toKey!]   = value?.to   ? new Date(value.to).toISOString()   : null;
        } else if (meta.filterType === 'numberRange') {
          self.filter[meta.fromKey!] = value?.from ?? null;
          self.filter[meta.toKey!]   = value?.to   ?? null;
        } else if (meta.filterType === 'checkbox') {
          // 0 ignore -> unset, 1 -> false, 2 -> true
          if (value === 0 || value === '0') delete self.filter[meta.key!];
          else if (value === 1 || value === '1') self.filter[meta.key!] = false;
          else if (value === 2 || value === '2') self.filter[meta.key!] = true;
        } else {
          self.filter[meta.key!] = value ?? '';
        }
        self.selectedPage = 1;
        self.emitSearch();
      },
      getSort: () => {
        const sb = (self.filter && self.filter.sortBy) || null;
        return { key: sb?.propertyName, dir: sb ? (sb.ascending ? 'asc' : 'desc') : '' };
      },
      setSort: (key?: string, dir: SortDir = '') => {
        if (!key || !dir) {
          self.filter.sortBy = null;
        } else {
          self.filter.sortBy = { propertyName: key, ascending: dir === 'asc' };
        }
        self.emitSearch();
      }
    };
    return { [OP_TABLE_CTX as any]: ctx };
  }
})
export default class OpTableServerSide extends Vue {
  @Prop({ default: () => [] }) items!: any[];
  @Prop({ default: true }) showUpperPagination!: boolean;
  @Prop({ default: true }) showLowerPagination!: boolean;
  @Prop({ default: 10 }) itemsPerPage!: number;
  @Prop({ default: () => ({}) }) filter!: any;
  @Prop({ default: 0, required: true }) totalItemsCount!: number;

  selectedPage = 1; xItemsPerPage = 10; pages:number[] = []; totalPages=0; lowerPages=0; upperPages=0;

  // shadow per ripopolare input header
  uiFilterShadow: Record<string, any> = {};
  ready = false;

  created(){
    // ripristina pagina e dimensione salvate nei filtri (StorageServices)
    this.selectedPage = this.filter?.pageNumber || 1;
    this.xItemsPerPage = this.filter?.perPage || this.itemsPerPage;
    // ripopola gli input dei filtri semplici con i valori salvati
    for (const k in this.filter || {}) this.uiFilterShadow[k] = this.filter[k];
  }
  mounted(){
    // sposta le classi dal div root al <table>, come opTableLocal
    const rootElement: any = this.$refs.rootElement;
    const classes: string[] = []; rootElement.classList.forEach((x: string) => classes.push(x));
    rootElement.classList.value = '';
    const tableRoot = this.$refs.tableRoot as HTMLElement;
    classes.forEach(x => tableRoot.classList.add(x));

    this.calcPagination();
    this.ready = true;
  }
  @Watch('xItemsPerPage') onIPP(){ if(!this.ready) return; this.selectedPage = 1; this.calcPagination(); this.emitSearch(); }
  @Watch('totalItemsCount') onCount(){ this.calcPagination(); }

  emitSearch(){
    this.filter.pageNumber = this.selectedPage;
    this.filter.perPage = this.xItemsPerPage;
    this.$emit('search', this.filter);
  }

  setSelectedPage(i:number){ this.selectedPage = i; this.calcPagination(); this.emitSearch(); }
  subtractPage(){ if(this.selectedPage>1){ this.selectedPage--; this.calcPagination(); this.emitSearch(); } }
  addPage(){ if(this.selectedPage<this.totalPages){ this.selectedPage++; this.calcPagination(); this.emitSearch(); } }

  calcPagination(){
    this.totalPages = Math.ceil((this.totalItemsCount) / this.xItemsPerPage) || 1;
    if(this.selectedPage > this.totalPages && this.selectedPage > 1) this.selectedPage = this.totalPages;
    this.lowerPages = this.selectedPage - 2;
    this.upperPages = this.selectedPage + 2;
    if(this.selectedPage == 1) this.upperPages += 2; else if(this.selectedPage == 2) this.upperPages += 1;
    if(this.selectedPage == this.totalPages) this.lowerPages -= 2; else if(this.selectedPage == this.totalPages - 1) this.lowerPages -= 1;
    if(this.upperPages > this.totalPages) this.upperPages = this.totalPages;
    if(this.lowerPages <= 0) this.lowerPages = 1;
    this.pages = []; for(let i=this.lowerPages; i<=this.upperPages; i++) this.pages.push(i);
  }
}
</script>
